import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async'; 

export default function Contact() {
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [inquiryType, setInquiryType] = useState('general');
  const [fallbackData, setFallbackData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus("Initiating atmospheric breach...");
    setFallbackData(null); // Reset fallback on new attempt

    const payload = {
      name: e.target.userName.value,
      email: e.target.userEmail.value,
      type: inquiryType,
      message: e.target.userMessage.value
    };

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setStatus("Transmission secured. The Architect will review your coordinates shortly.");
        e.target.reset();
      } else {
        throw new Error("Server rejected the transmission.");
      }
    } catch (err) {
      console.error(err);
      setStatus("Transmission failed. Matrix connection lost. Please use the secure manual override below.");
      // Trigger the failsafe so they can email you directly without losing their typed message
      setFallbackData(payload); 
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '80vh', padding: '60px 20px', background: '#05070a', color: '#c9d1d9', fontFamily: 'monospace' }}>
      
      <Helmet>
        <title>Media & VIP Uplink | Adonisian Astro-Somatic™</title>
        <meta name="description" content="Initiate a direct uplink with the Architect. Submit media/podcast inquiries or apply for a Private Astro-Somatic™ Consultation." />
      </Helmet>
      
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        
        <h1 style={{ color: '#ffca28', textAlign: 'center', fontSize: '2.5rem', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '15px' }}>
          Open Transmission
        </h1>
        
        <p style={{ color: '#8b949e', textAlign: 'center', fontSize: '1rem', maxWidth: '600px', margin: '0 auto 40px auto', lineHeight: '1.6' }}>
          The portal is currently restricted. Please use the terminal below to submit media/podcast inquiries, apply for a Private VIP Astro-Somatic Consultation, or send general community transmissions.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px' }}>
          
          {/* LEFT: THE UNICORN BIO */}
          <div style={{ flex: '1 1 350px', background: '#0d1117', padding: '30px', borderRadius: '12px', border: '1px solid #30363d' }}>
            <h2 style={{ color: '#00d4ff', marginTop: 0, fontSize: '1.5rem', textTransform: 'uppercase' }}>The Architect</h2>
            
            <p style={{ color: '#8b949e', fontSize: '1rem', lineHeight: '1.6' }}>
              Adonis Ramirez is an original Silicon Valley unicorn. Born in Mexico (1987), made in America (since 1990), Adonis was raised in the epicenter of East Palo Alto—recognized as the 1992 murder capital of the United States, even claiming his mother's life—and survived the chaos of the simulation to decode its matrix.
            </p>
            
            <p style={{ color: '#8b949e', fontSize: '1rem', lineHeight: '1.6' }}>
              He is a full-stack coder, bio-hacker, scientist, astronomer, mathematician, and writer. With two decades of dedicated kinetic practice, he is a certified yoga instructor and Ayurvedic practitioner who bridges the critical gap between ancient somatic movement and neuroscientific optimization.             
            </p>
            <div style={{ borderLeft: '3px solid #00ff41', paddingLeft: '15px', margin: '20px 0' }}>
              <p style={{ color: '#00ff41', fontStyle: 'italic', margin: 0, fontSize: '0.95rem' }}>
                "I didn't just look up at the stars; I calculated their true mechanical weight on the human nervous system."
              </p>
            </div>
            
            <p style={{ color: '#c9d1d9', fontSize: '0.9rem', marginTop: '30px' }}>
              <strong>Direct Link:</strong> adonis@adonisian.com<br/>
              <strong>Base Coordinates:</strong> Oakland / San Francisco Bay Area
            </p>
          </div>

          {/* RIGHT: THE RESTRICTED CONTACT FORM */}
          <div style={{ flex: '1 1 400px' }}>
            
            <div style={{ padding: '15px', background: 'rgba(255, 202, 40, 0.05)', border: '1px dashed #ffca28', borderRadius: '6px', color: '#ffca28', marginBottom: '25px', fontSize: '0.85rem', lineHeight: '1.5' }}>
              <p style={{ margin: '0 0 10px 0' }}>
                <strong>System Notice:</strong> If you attempted to initiate a transmission prior to June 2026, atmospheric interference prevented the signal from reaching the Architect. The matrix routing is now fully repaired. Please resend your uplink below.
              </p>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              <div>
                <label style={{ color: '#8b949e', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>Clearance Level (Inquiry Type)</label>
                <select 
                  value={inquiryType} 
                  onChange={(e) => setInquiryType(e.target.value)}
                  style={{ width: '100%', padding: '15px', background: '#05070a', border: '1px solid #00d4ff', color: '#00d4ff', borderRadius: '6px', fontFamily: 'monospace', boxSizing: 'border-box', cursor: 'pointer', appearance: 'none' }}
                >
                  <option value="media">🎙️ Press, Podcast & Media Inquiry</option>
                  <option value="vip">🌌 Private Consultation Application (VIP)</option>
                  <option value="general">📡 General Community Transmission</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 45%' }}>
                  <label style={{ color: '#8b949e', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>Your Identity (Name)</label>
                  <input required name="userName" type="text" style={{ width: '100%', padding: '15px', background: '#0d1117', border: '1px solid #30363d', color: 'white', borderRadius: '6px', fontFamily: 'monospace', boxSizing: 'border-box' }} />
                </div>

                <div style={{ flex: '1 1 45%' }}>
                  <label style={{ color: '#8b949e', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>Return Coordinates (Email)</label>
                  <input required name="userEmail" type="email" style={{ width: '100%', padding: '15px', background: '#0d1117', border: '1px solid #30363d', color: 'white', borderRadius: '6px', fontFamily: 'monospace', boxSizing: 'border-box' }} />
                </div>
              </div>

              <div>
                <label style={{ color: '#8b949e', fontSize: '0.9rem', display: 'block', marginBottom: '8px' }}>Transmission Data (Message)</label>
                <textarea required name="userMessage" rows="6" placeholder={inquiryType === 'vip' ? "Briefly explain your current astrosomatic or financial blockages..." : "Enter your message or media pitch..."} style={{ width: '100%', padding: '15px', background: '#0d1117', border: '1px solid #30363d', color: 'white', borderRadius: '6px', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }}></textarea>
              </div>

              <button type="submit" disabled={loading} style={{ background: loading ? '#30363d' : '#ffca28', color: loading ? '#8b949e' : '#05070a', border: 'none', padding: '15px', fontSize: '1.1rem', fontWeight: 'bold', textTransform: 'uppercase', borderRadius: '6px', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', boxShadow: '0 0 15px rgba(255, 202, 40, 0.2)' }}>
                {loading ? "Transmitting..." : "Initiate Uplink"}
              </button>

              {status && (
                <div style={{ padding: '15px', background: fallbackData ? 'rgba(255, 64, 129, 0.1)' : 'rgba(0, 255, 65, 0.1)', border: `1px solid ${fallbackData ? '#ff4081' : '#00ff41'}`, borderRadius: '6px', color: fallbackData ? '#ff4081' : '#00ff41', textAlign: 'center', fontSize: '0.9rem' }}>
                  {status}
                  
                  {/* THE FAILSAFE BUTTON */}
                  {fallbackData && (
                    <div style={{ marginTop: '15px' }}>
                      <a 
                        href={`mailto:adonis@adonisian.com?subject=Adonisian Uplink: ${fallbackData.type.toUpperCase()} from ${fallbackData.name}&body=${encodeURIComponent(fallbackData.message)}%0A%0A--%0AReturn Email: ${fallbackData.email}`}
                        style={{ display: 'inline-block', background: '#ff4081', color: 'white', padding: '10px 20px', borderRadius: '4px', textDecoration: 'none', fontWeight: 'bold', letterSpacing: '1px' }}
                      >
                        MANUAL OVERRIDE: SEND DIRECT EMAIL
                      </a>
                    </div>
                  )}
                </div>
              )}
            </form>
          </div>

        </div>
      </div>
    </div>
  );
}